import { useState } from 'react'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'

/**
 * The invitation link, shown and copyable — the one affordance that makes an
 * invitation acceptable from a browser at all.
 *
 * **Extracted rather than copied.** `InviteDialog`'s success panel had this
 * first; Task 10 needs the identical thing on a row after a resend rotates the
 * token. That would have been the second copy of the clipboard handling, the
 * secure-context fallback and the read-only field, and this repo's own history
 * — three progress rings on iOS, `focusFirstAlive` existing because one idea
 * had three implementations — says the second copy is where to stop.
 *
 * **The link is SHOWN, not only copied.** `navigator.clipboard` is
 * secure-context-only and simply absent on the plain-http LAN origin this
 * project uses for real-device testing, so a Copy button is not on its own a
 * way to get a link out of the page. The read-only field is what makes it
 * recoverable, and the button's failure arm points at it.
 *
 * **`token === null` is rendered, not assumed away.** The schema types it
 * nullable and the whole affordance depends on it; a panel built around a link
 * that is not there renders `…/invite/null`, which looks like a link and is
 * not one.
 */
export function inviteLinkFor(token: string | null): string | null {
  // Mirrors `App.tsx`'s `INVITE_PATH` (`/invite/:token`), built here rather
  // than imported: `App.tsx` imports every route module, so reaching back into
  // it from one of them is a cycle for the sake of one string. Issue #352
  // tracks the guard that neither spelling has — an e2e that NAVIGATES this
  // link rather than comparing it to the constant it was built from.
  return token === null ? null : `${window.location.origin}/invite/${token}`
}

/** A union rather than two booleans: `copied && failed` is unreachable and
 *  should be unrepresentable — the reasoning `Dialog.tsx`'s `Status` gives. */
type CopyState = 'idle' | 'copied' | 'failed'

export function InviteLink({
  token,
  label = 'Invite link',
  unavailable = 'The invitation was sent, but its link is unavailable here.',
  children,
}: {
  token: string | null
  label?: string
  /** What to say when the server returned no token. */
  unavailable?: string
  /** Context above the link — why it matters, or that a previous one is dead. */
  children?: React.ReactNode
}) {
  const [copyState, setCopyState] = useState<CopyState>('idle')
  const link = inviteLinkFor(token)

  async function copy() {
    if (link === null) return
    const clipboard = navigator.clipboard
    if (clipboard === undefined) {
      setCopyState('failed')
      return
    }
    try {
      await clipboard.writeText(link)
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  if (link === null) {
    return <p className="invite-link__note">{unavailable}</p>
  }

  return (
    <div className="invite-link">
      {children}
      <Field label={label} size="form" readOnly value={link} />
      <div className="invite-link__actions">
        <Button type="button" variant="outline" size="form" onClick={() => void copy()}>
          Copy invite link
        </Button>
        {copyState !== 'idle' && (
          <span className="invite-link__note" role="status">
            {copyState === 'copied'
              ? 'Link copied.'
              : 'Copying is unavailable here — select the link above instead.'}
          </span>
        )}
      </div>
    </div>
  )
}
